import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import type {ReactNode} from 'react';
import {useLocation} from 'react-router-dom';
import {useEnrollGroup} from '@client/pages/public/enroll/enrollContext';
import {EnrollEvent, useEnrollAnalytics} from '@client/pages/public/enroll/enrollAnalytics';
import type {CoverageTier, GroupConfig, ProductCategory} from '@client/pages/public/enroll/types';
import {COVERAGE_TIER_LABELS} from '@client/pages/public/enroll/types';
import type {
  Applicant,
  Beneficiary,
  Dependent,
  EnrollSelections,
  EnrollStep,
  EnrollStepsState,
  EnrollTiers,
  Selection,
} from '@client/pages/public/enroll/enrollModel';
import {
  buildSteps,
  EMPTY_APPLICANT,
  EMPTY_BENEFICIARY,
  isStepComplete,
  needsBeneficiary,
  selectionTotal,
  effectiveTiers,
  tierFor,
} from '@client/pages/public/enroll/enrollModel';
import type {EnrollmentConfirmation} from '@client/pages/public/enroll/confirmation/enrollmentApi';

interface EnrollStepsContextValue {
  group: GroupConfig;
  tiers: EnrollTiers;
  selections: EnrollSelections;
  applicant: Applicant;
  dependents: Dependent[];
  beneficiary: Beneficiary;
  /** Whether a chosen plan asks for a beneficiary at checkout. */
  beneficiaryRequired: boolean;
  enrollmentReceipt: EnrollmentConfirmation | null;
  steps: EnrollStep[];
  currentStep: EnrollStep | undefined;
  currentIndex: number;
  /** Furthest step index the user has reached; drives "passed once" tracker checks. */
  maxReachedIndex: number;
  total: number;
  getTier: (category: ProductCategory) => CoverageTier;
  setTier: (category: ProductCategory, tier: CoverageTier) => void;
  selectPlan: (category: ProductCategory, planId: string) => void;
  declineProduct: (category: ProductCategory) => void;
  clearSelection: (category: ProductCategory) => void;
  setApplicant: (patch: Partial<Applicant>) => void;
  setDependents: (dependents: Dependent[]) => void;
  setBeneficiary: (beneficiary: Beneficiary) => void;
  setEnrollmentReceipt: (receipt: EnrollmentConfirmation) => void;
  clearEnrollmentReceipt: () => void;
  reset: () => void;
  isStepComplete: (step: EnrollStep) => boolean;
  canContinue: boolean;
}

const EnrollStepsContext = createContext<EnrollStepsContextValue | null>(null);

const DEFAULT_STATE: EnrollStepsState = {
  tiers: {},
  selections: {},
  applicant: EMPTY_APPLICANT,
  dependents: [],
  beneficiary: EMPTY_BENEFICIARY,
};

function storageKey(slug: string): string {
  return `enroll:${slug}`;
}

function receiptKey(slug: string): string {
  return `enroll:${slug}:receipt`;
}

// Never persist raw SSNs to sessionStorage (plaintext, XSS-readable). In-progress SSNs are dropped
// entirely (re-entered on refresh); the receipt keeps only the last 4 (all the UI ever displays).
function maskSsnLast4(ssn: string): string {
  const last4 = ssn.replace(/\D/g, '').slice(-4);
  return last4 ? `***-**-${last4}` : '';
}

function stripStateSsns(state: EnrollStepsState): EnrollStepsState {
  return {
    ...state,
    applicant: {...state.applicant, ssn: ''},
    dependents: state.dependents.map((dependent) => ({...dependent, ssn: ''})),
  };
}

function maskReceiptSsns(receipt: EnrollmentConfirmation): EnrollmentConfirmation {
  return {
    ...receipt,
    subscriber: {...receipt.subscriber, ssn: maskSsnLast4(receipt.subscriber.ssn)},
    dependents: receipt.dependents.map((dependent) => ({
      ...dependent,
      ssn: maskSsnLast4(dependent.ssn),
    })),
  };
}

function loadReceiptFromSession(slug: string): EnrollmentConfirmation | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(receiptKey(slug));
    return raw ? (JSON.parse(raw) as EnrollmentConfirmation) : null;
  } catch {
    return null;
  }
}

function isCoverageTier(value: unknown): value is CoverageTier {
  return typeof value === 'string' && Object.hasOwn(COVERAGE_TIER_LABELS, value);
}

// Tiers feed rate lookups (plan.rates[tier]); drop any stale/invalid session entries.
function sanitizeTiers(value: unknown): EnrollTiers {
  if (!value || typeof value !== 'object') return {};
  const result: EnrollTiers = {};
  for (const [category, tier] of Object.entries(value)) {
    if (isCoverageTier(tier)) result[category as ProductCategory] = tier;
  }
  return result;
}

// Selections drive pricing/review; keep only well-formed {planId?} / {declined?} session entries.
function sanitizeSelections(value: unknown): EnrollSelections {
  if (!value || typeof value !== 'object') return {};
  const result: EnrollSelections = {};
  for (const [category, sel] of Object.entries(value)) {
    if (!sel || typeof sel !== 'object') continue;
    const {planId, declined} = sel as {planId?: unknown; declined?: unknown};
    const clean: Selection = {};
    if (typeof planId === 'string') clean.planId = planId;
    if (typeof declined === 'boolean') clean.declined = declined;
    if (clean.planId !== undefined || clean.declined !== undefined) {
      result[category as ProductCategory] = clean;
    }
  }
  return result;
}

function loadStateFromSession(slug: string): EnrollStepsState {
  if (typeof window === 'undefined') return DEFAULT_STATE;
  try {
    const raw = window.sessionStorage.getItem(storageKey(slug));
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw) as Partial<EnrollStepsState>;
    return {
      tiers: sanitizeTiers(parsed.tiers),
      selections: sanitizeSelections(parsed.selections),
      applicant: {...DEFAULT_STATE.applicant, ...parsed.applicant},
      dependents: Array.isArray(parsed.dependents) ? parsed.dependents : [],
      beneficiary: {...DEFAULT_STATE.beneficiary, ...parsed.beneficiary},
    };
  } catch {
    return DEFAULT_STATE;
  }
}

function currentStepFromPath(pathname: string, steps: EnrollStep[]): EnrollStep | undefined {
  const segment = pathname.split('/').filter(Boolean).pop();
  return steps.find((step) => step === segment);
}

export function EnrollStepsProvider({children}: {children: ReactNode}) {
  const group = useEnrollGroup();
  const location = useLocation();
  const {track} = useEnrollAnalytics();
  const [enrollState, setEnrollState] = useState<EnrollStepsState>(() =>
    loadStateFromSession(group.slug),
  );
  const [enrollmentReceipt, setEnrollmentReceiptState] = useState<EnrollmentConfirmation | null>(
    () => loadReceiptFromSession(group.slug),
  );

  // Persist enrollment state to sessionStorage on change, skipping the initial render
  // so we don't immediately rewrite the value we just hydrated from storage.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    try {
      window.sessionStorage.setItem(
        storageKey(group.slug),
        JSON.stringify(stripStateSsns(enrollState)),
      );
    } catch {
      // sessionStorage unavailable (private mode / SSR) — selections stay in-memory only.
    }
  }, [group.slug, enrollState]);

  const setTier = useCallback((category: ProductCategory, tier: CoverageTier) => {
    setEnrollState((prev) => ({...prev, tiers: {...prev.tiers, [category]: tier}}));
  }, []);

  const selectPlan = useCallback(
    (category: ProductCategory, planId: string) => {
      const tier = tierFor(effectiveTiers(group, enrollState.tiers), category);
      setEnrollState((prev) => ({
        ...prev,
        selections: {...prev.selections, [category]: {planId}},
      }));
      track(EnrollEvent.PlanSelected, {
        category,
        plan_id: planId,
        tier,
      });
    },
    [group, enrollState.tiers, track],
  );

  const declineProduct = useCallback(
    (category: ProductCategory) => {
      track(EnrollEvent.PlanDeclined, {category});
      setEnrollState((prev) => ({
        ...prev,
        selections: {...prev.selections, [category]: {declined: true}},
      }));
    },
    [track],
  );

  const clearSelection = useCallback((category: ProductCategory) => {
    setEnrollState((prev) => {
      const selections = {...prev.selections};
      delete selections[category];
      return {...prev, selections};
    });
  }, []);

  const setApplicant = useCallback((patch: Partial<Applicant>) => {
    setEnrollState((prev) => ({...prev, applicant: {...prev.applicant, ...patch}}));
  }, []);

  const setDependents = useCallback((dependents: Dependent[]) => {
    setEnrollState((prev) => ({...prev, dependents}));
  }, []);

  const setBeneficiary = useCallback((beneficiary: Beneficiary) => {
    setEnrollState((prev) => ({...prev, beneficiary}));
  }, []);

  const setEnrollmentReceipt = useCallback(
    (next: EnrollmentConfirmation) => {
      setEnrollmentReceiptState(next);
      try {
        window.sessionStorage.setItem(
          receiptKey(group.slug),
          JSON.stringify(maskReceiptSsns(next)),
        );
      } catch {
        // sessionStorage unavailable — receipt stays in-memory only.
      }
    },
    [group.slug],
  );

  const clearEnrollmentReceipt = useCallback(() => {
    setEnrollmentReceiptState(null);
    try {
      window.sessionStorage.removeItem(receiptKey(group.slug));
    } catch {
      // ignore
    }
  }, [group.slug]);

  const reset = useCallback(() => {
    try {
      window.sessionStorage.removeItem(storageKey(group.slug));
      window.sessionStorage.removeItem(receiptKey(group.slug));
    } catch {
      // ignore
    }
    setEnrollState(DEFAULT_STATE);
    setEnrollmentReceiptState(null);
    setMaxReachedIndex(-1);
  }, [group.slug]);

  const steps = useMemo(() => buildSteps(group), [group]);
  const currentStep = currentStepFromPath(location.pathname, steps);
  const currentIndex = currentStep ? steps.indexOf(currentStep) : -1;

  // Track the furthest step reached so the tracker can tell "passed once" from "not yet reached".
  const [maxReachedIndex, setMaxReachedIndex] = useState(-1);
  useEffect(() => {
    setMaxReachedIndex((prev) => (currentIndex > prev ? currentIndex : prev));
  }, [currentIndex]);
  // A tier left over for an employee-only product (e.g. from an earlier session) is never priced or sent.
  const tiers = useMemo(() => effectiveTiers(group, enrollState.tiers), [group, enrollState.tiers]);
  const total = useMemo(
    () => selectionTotal(group, tiers, enrollState.selections),
    [group, tiers, enrollState.selections],
  );

  const getTier = useCallback((category: ProductCategory) => tierFor(tiers, category), [tiers]);

  const isStepCompleteFn = useCallback(
    (step: EnrollStep) => isStepComplete(group, enrollState, step),
    [group, enrollState],
  );

  const canContinue = currentStep ? isStepCompleteFn(currentStep) : false;

  const value: EnrollStepsContextValue = {
    group,
    tiers,
    selections: enrollState.selections,
    applicant: enrollState.applicant,
    dependents: enrollState.dependents,
    beneficiary: enrollState.beneficiary,
    beneficiaryRequired: needsBeneficiary(group, enrollState.selections),
    enrollmentReceipt,
    steps,
    currentStep,
    currentIndex,
    maxReachedIndex,
    total,
    getTier,
    setTier,
    selectPlan,
    declineProduct,
    clearSelection,
    setApplicant,
    setDependents,
    setBeneficiary,
    setEnrollmentReceipt,
    clearEnrollmentReceipt,
    reset,
    isStepComplete: isStepCompleteFn,
    canContinue,
  };

  return <EnrollStepsContext.Provider value={value}>{children}</EnrollStepsContext.Provider>;
}

export function useEnrollSteps(): EnrollStepsContextValue {
  const ctx = useContext(EnrollStepsContext);
  if (!ctx) {
    throw new Error('useEnrollSteps must be used within an EnrollStepsProvider');
  }
  return ctx;
}
